import { Locale } from '@repo/utils'
import { IntlShape } from 'react-intl'
import { toast } from 'sonner'

import { IQuoteProvisionInputProps } from '../create-or-update-quote-provision-modal/CreateOrUpdateQuoteProvisionModal'
import { getVatRateOptions } from '../create-or-update-quote-provision-modal/utils/utils'
import { IQuoteSectionBodyTableRowProps } from '../quote-section-body/quote-section-body-table/quote-section-body-table-row/QuoteSectionBodyTableRow'
import { useLocale } from '../../../../../../../hooks/use-locale'
import {
  CurrencyAreaOutputDto,
  QuoteSectionClientProposalPricingItemFragment,
} from '../../../../../../../lib/graphql'
import { percentage } from '../../../../../../../utils'
import { divideByFactor } from '../../../../../../../utils/prices'
import { VAT_STATUS } from '../../../../../../contexts'
import { IProposalInteractionsProps } from '../../../../../../organisms/proposal/Proposal'

const formatClientPricingItem = ({
  clientPricingItem,
  currencyArea,
  vatStatus,
  locale,
}: {
  clientPricingItem: QuoteSectionClientProposalPricingItemFragment
  currencyArea: CurrencyAreaOutputDto
  vatStatus: VAT_STATUS
  locale: Locale
}): IQuoteProvisionInputProps => {
  const unitPrice =
    vatStatus === VAT_STATUS.HT
      ? clientPricingItem.unitPrice?.basePrice?.priceWithoutVat
      : clientPricingItem.unitPrice?.basePrice?.priceWithVat
  const totalPrice =
    vatStatus === VAT_STATUS.HT
      ? clientPricingItem.totalPrice?.basePrice?.priceWithVat
      : clientPricingItem.totalPrice?.basePrice?.priceWithoutVat
  return {
    _id: clientPricingItem._id,
    name: clientPricingItem.objectData.label,
    description: clientPricingItem.objectData.description ?? '',
    category: clientPricingItem.category,
    type: clientPricingItem.type,
    vatStatus,
    unitPrice: unitPrice ? divideByFactor(unitPrice).toString() : undefined,
    vatRateIndex: getVatRateIndex({ clientPricingItem, currencyArea, locale }),
    quantity: clientPricingItem.priceOption.quantity?.toString(),
    unit: clientPricingItem.priceUnit ?? undefined,
    totalPrice: totalPrice ? divideByFactor(totalPrice).toString() : undefined,
  }
}

const getVatRateIndex = ({
  clientPricingItem,
  currencyArea,
  locale,
}: {
  clientPricingItem: QuoteSectionClientProposalPricingItemFragment
  currencyArea: CurrencyAreaOutputDto
  locale: Locale
}) => {
  const price =
    clientPricingItem.objectData.prices.find(
      (price) =>
        price._id.toString() === clientPricingItem.option.priceId?.toString(),
    ) ?? clientPricingItem.objectData.prices[0]

  const vatRates = price.vatPrices
    .map((vatPrice) => vatPrice.vatRate)
    .filter(
      (vatRate): vatRate is number => vatRate !== undefined && vatRate !== null,
    ) // Type guard
    .sort((a, b) => a - b)

  const formattedVatRates = vatRates
    .map((vatRate, index) => {
      if (index === vatRates.length - 1) {
        return percentage({ rate: vatRate, locale })
      }
      return divideByFactor(vatRate)
    })
    .join(' - ')

  const vatRateOptions = getVatRateOptions({ currencyArea, locale })
  const vatRateIndex = vatRateOptions.findIndex(
    (option) => option.label === formattedVatRates,
  )
  if (vatRateIndex === -1) {
    return undefined
  }
  return vatRateIndex.toString()
}

export const getTableRowActions = ({
  onOpenChange,
  clientProposalId,
  clientPricingItems,
  setSelectedClientPricingItem,
  currencyArea,
  ownerActions,
  canUpdateQuote,
  intl,
}: {
  onOpenChange: (open: boolean) => void
  clientProposalId: string
  clientPricingItems: QuoteSectionClientProposalPricingItemFragment[]
  setSelectedClientPricingItem: React.Dispatch<
    React.SetStateAction<IQuoteProvisionInputProps | undefined>
  >
  currencyArea: CurrencyAreaOutputDto
  ownerActions: IProposalInteractionsProps['ownerActions']
  canUpdateQuote: boolean
  intl: IntlShape
}): IQuoteSectionBodyTableRowProps['actions'] => {
  const { locale } = useLocale()
  if (!canUpdateQuote) {
    return []
  }
  return [
    {
      icon: { name: 'edit', size: 12 },
      onClick: ({ clientPricingItemId, vatStatus }) => {
        onOpenChange(true)
        const clientPricingItem = clientPricingItems.find(
          (item) => item._id === clientPricingItemId,
        )
        if (clientPricingItem && vatStatus) {
          const formattedClientPricingItem = formatClientPricingItem({
            clientPricingItem,
            currencyArea,
            vatStatus,
            locale,
          })
          setSelectedClientPricingItem(formattedClientPricingItem)
        }
      },
    },
    {
      icon: { name: 'copy', size: 12 },
      onClick: ({ clientPricingItemId, vatStatus }) => {
        onOpenChange(true)
        const clientPricingItem = clientPricingItems.find(
          (item) => item._id === clientPricingItemId,
        )
        if (clientPricingItem && vatStatus) {
          const formattedClientPricingItem = formatClientPricingItem({
            clientPricingItem,
            currencyArea,
            vatStatus,
            locale,
          })
          setSelectedClientPricingItem({
            ...formattedClientPricingItem,
            _id: undefined,
            isDuplicated: true,
          })
        }
      },
    },
    {
      icon: { name: 'delete', size: 16 },
      onClick: ({ clientPricingItemId }) => {
        if (clientPricingItemId) {
          const clientPricingItem = clientPricingItems.find(
            (item) => item._id === clientPricingItemId,
          )

          ownerActions?.ownerDeletedQuoteProvision.callback({
            clientPricingItemId,
            clientProposalId,
          })

          toast(
            intl.formatMessage({
              id: 'ui.getTableRowActions.toastTitle',
              defaultMessage: 'Prestation supprimée',
            }),
            {
              description: intl.formatMessage(
                {
                  id: 'ui.getTableRowActions.toastDescription',
                  defaultMessage: `"{clientPricingItemLabel}" vient d'être supprimée`,
                },
                {
                  clientPricingItemLabel: clientPricingItem?.objectData.label,
                },
              ),
              action: {
                label: intl.formatMessage({
                  id: 'ui.getTableRowActions.cancel',
                  defaultMessage: 'Annuler',
                }),
                onClick: () => {
                  ownerActions?.ownerCanceledQuoteProvisionSuppression.callback(
                    {
                      clientPricingItemId,
                      clientProposalId,
                    },
                  )
                },
              },
            },
          )
        }
      },
    },
  ]
}
